import React, { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Loader2, Mic, CheckCircle2, AlertTriangle, Wand2 } from "lucide-react";

const QUESTIONS = [
  ["q1", "Overall trajectory", "Over the whole period, what stands out most about this student's intellectual development? What changed most from beginning to end?"],
  ["q2", "Acquiring new knowledge", "When they meet something genuinely new/advanced, how do they respond? How quickly do they understand, and how much explanation/repetition do they need? Give an example."],
  ["q3", "Reasoning & problem solving", "Facing a problem whose answer wasn't shown first, what do they do? Can they reason independently, defend an answer, recover when the first approach fails? (maths/philosophy/science example)"],
  ["q4", "Transfer", "Have you seen them use something learned in one context to solve/explain something different, without being told to connect them? What happened? (key for N4/N5)"],
  ["q5", "Explanation & teach-back", "How well can they explain what they learned in their own words — structure a conference, answer questions, teach it to someone? Strongest example?"],
  ["q6", "Autonomy, difficulty & metacognition", "When learning gets hard/confusing, do they persist, ask for help, recover, identify what they don't understand, change strategy? How much adult support is usually needed?"],
  ["q7", "Research, creativity & collaboration", "When asked to research independently, create something original, or work with others — curiosity, initiative, quality of ideas, contribution, completion?"],
  ["q8", "Domain mastery & next step", "Across subjects actually studied this period, what could they merely encounter, understand, apply, transfer, or explain/teach? What should we challenge them with next?"],
];

const CONF = { High: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40", Moderate: "bg-amber-500/20 text-amber-300 border-amber-400/40", Low: "bg-slate-500/20 text-slate-300 border-slate-400/40" };

export default function SITFCopilot({ aid, student, onApply, onClose }) {
  const [mode, setMode] = useState("guided");
  const [answers, setAnswers] = useState({});
  const [fast, setFast] = useState("");
  const [busy, setBusy] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [applied, setApplied] = useState({});

  const voice = (setter, cur) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return toast.error("Voice dictation isn't supported in this browser");
    const r = new SR(); r.lang = "en-US"; r.interimResults = false;
    r.onresult = (e) => setter((cur ? cur + " " : "") + e.results[0][0].transcript);
    r.start();
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/sitf/assessments/${aid}/copilot`, { mode, answers, fast_text: fast });
      setProposal(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const CRIT = useMemo(() => proposal ? Object.keys(proposal.suggestions) : [], [proposal]);
  const cellFor = (s) => ({ level: s.level, trajectory: s.trajectory, rationale: s.rationale,
    evidence: s.evidence ? [{ type: "teacher_note", note: s.evidence, date: "", activity: "", url: "", tag: "" }] : [] });

  const acceptOne = (ck) => { onApply({ levels: { [ck]: cellFor(proposal.suggestions[ck]) } }); setApplied((p) => ({ ...p, [ck]: true })); };
  const acceptHigh = () => {
    const lv = {}; CRIT.forEach((ck) => { if (proposal.suggestions[ck].confidence === "High") lv[ck] = cellFor(proposal.suggestions[ck]); });
    onApply({ levels: lv }); setApplied((p) => ({ ...p, ...Object.fromEntries(Object.keys(lv).map((k) => [k, true])) }));
    toast.success(`Applied ${Object.keys(lv).length} high-confidence proposals`);
  };
  const applyAll = () => {
    const lv = {}; CRIT.forEach((ck) => (lv[ck] = cellFor(proposal.suggestions[ck])));
    onApply({ levels: lv, domains: proposal.domains, strengths: proposal.strengths, priorities: proposal.priorities,
      primary_priority: proposal.primary_priority || "", recommended_track: proposal.recommended_track || "", qualification: proposal.qualification || {} });
    toast.success("All proposals applied — review each and Save");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="sitf-copilot-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#0B0D12] border border-white/10 rounded-md w-full max-w-3xl max-h-[88vh] overflow-auto">
        <div className="flex items-center justify-between px-6 h-14 border-b border-white/10 sticky top-0 bg-[#0B0D12] z-10">
          <div className="font-serif text-lg flex items-center gap-2"><Wand2 className="h-5 w-5 text-heraldic" /> SITF Assessment Copilot — {student?.name}</div>
          <button onClick={onClose}><X className="h-5 w-5 text-paper/50" /></button>
        </div>
        <div className="p-6">
          <p className="text-xs text-paper/50 mb-4">The Copilot proposes; you decide. Nothing enters the record until you Accept and Save. Where evidence is insufficient it proposes <b>N0 — Not Yet Observed</b>.</p>

          {!proposal && (
            <>
              <div className="flex gap-2 mb-4">
                {[["guided", "Guided (8 questions)"], ["fast", "Fast (speak freely)"]].map(([k, l]) => (
                  <button key={k} onClick={() => setMode(k)} data-testid={`copilot-mode-${k}`} className={`text-xs px-3 py-1.5 rounded-full border ${mode === k ? "bg-heraldic text-white border-heraldic" : "border-white/15 text-paper/60"}`}>{l}</button>
                ))}
              </div>
              {mode === "guided" ? (
                <div className="space-y-4">
                  {QUESTIONS.map(([q, title, prompt], i) => (
                    <div key={q}>
                      <div className="text-sm font-medium">{i + 1}. {title}</div>
                      <div className="text-xs text-paper/40 mb-1">{prompt}</div>
                      <div className="relative">
                        <textarea rows={2} value={answers[q] || ""} onChange={(e) => setAnswers({ ...answers, [q]: e.target.value })} data-testid={`copilot-${q}`} className="w-full bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm pr-9" />
                        <button onClick={() => voice((t) => setAnswers((a) => ({ ...a, [q]: t })), answers[q])} className="absolute right-2 top-2 text-paper/40 hover:text-heraldic" title="Dictate"><Mic className="h-4 w-4" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="relative">
                  <textarea rows={10} value={fast} onChange={(e) => setFast(e.target.value)} data-testid="copilot-fast" placeholder="Speak or type freely for 2–3 minutes about the student. The Copilot maps everything it can and marks the rest N0 / Not Assessed." className="w-full bg-[#0B0D12] border border-white/10 rounded-sm px-3 py-2 text-sm pr-9" />
                  <button onClick={() => voice(setFast, fast)} className="absolute right-2 top-2 text-paper/40 hover:text-heraldic" title="Dictate"><Mic className="h-4 w-4" /></button>
                </div>
              )}
              <button onClick={generate} disabled={busy} data-testid="copilot-generate" className="mt-4 inline-flex items-center gap-1.5 bg-heraldic text-white px-5 py-2.5 rounded-sm text-sm disabled:opacity-60">{busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Mapping evidence…</> : <><Sparkles className="h-4 w-4" /> Generate proposal</>}</button>
            </>
          )}

          {proposal && (
            <div data-testid="copilot-proposal">
              <div className="flex flex-wrap items-center gap-3 border border-white/10 rounded-sm p-3 bg-white/5">
                <div className="text-sm">Evidence coverage: <b className="text-heraldic">{proposal.stats.coverage}%</b></div>
                <div className="text-xs text-paper/50">High {proposal.stats.high} · Moderate {proposal.stats.moderate} · Low/insufficient {proposal.stats.low}</div>
                <div className="flex-1" />
                <button onClick={acceptHigh} data-testid="copilot-accept-high" className="text-xs border border-white/15 px-3 py-1.5 rounded-sm">Accept all high-confidence</button>
                <button onClick={applyAll} data-testid="copilot-apply-all" className="text-xs bg-heraldic text-white px-3 py-1.5 rounded-sm">Apply everything</button>
              </div>
              {proposal.follow_ups?.length > 0 && (
                <div className="mt-3 border border-amber-400/30 bg-amber-500/10 rounded-sm p-3 text-xs text-amber-200/90" data-testid="copilot-followups">
                  <div className="font-medium mb-1">Targeted follow-ups</div>
                  <ul className="list-disc pl-5 space-y-1">{proposal.follow_ups.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>
              )}
              <div className="mt-3 space-y-2">
                {CRIT.map((ck) => { const s = proposal.suggestions[ck]; return (
                  <div key={ck} className="border border-white/10 rounded-sm p-3" data-testid={`copilot-crit-${ck}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm capitalize">{ck.replace(/_/g, " ")}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-heraldic font-semibold text-sm">{s.level}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${CONF[s.confidence] || CONF.Low}`}>{s.confidence}</span>
                        <button onClick={() => acceptOne(ck)} data-testid={`copilot-accept-${ck}`} className={`text-xs px-2 py-1 rounded-sm ${applied[ck] ? "text-emerald-400" : "text-heraldic underline"}`}>{applied[ck] ? "✓ Applied" : "Accept"}</button>
                      </div>
                    </div>
                    {s.evidence && <div className="text-xs text-paper/60 mt-1"><b>Evidence:</b> {s.evidence}</div>}
                    {s.rationale && <div className="text-xs text-paper/40 mt-0.5">{s.rationale}</div>}
                  </div>
                ); })}
              </div>
              <button onClick={() => setProposal(null)} className="mt-4 text-xs text-paper/50 underline">← Back to interview</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
